import { errorReporter } from '@/services/errorReportingService';

export const AccountStatusTypes = {
  TRIAL: 'TRIAL',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  INACTIVE: 'INACTIVE',
  DELETED: 'DELETED',
};

export type AccountStatusType = (typeof AccountStatusTypes)[keyof typeof AccountStatusTypes];

export const getStatus = (
  deletionDate: string | null | undefined,
  subscriptionStatus: string | null | undefined,
): AccountStatusType => {
  const now = new Date();
  const deletion = deletionDate ? new Date(deletionDate) : null;

  // Define all status mappings explicitly for clarity and maintainability
  // Statuses that grant full access (ACTIVE)
  // 'trialing' = legitimate Stripe trial period, user should have access
  const activeStatuses = ['active', 'free', 'trialing'];
  // Statuses that grant limited/trial access (TRIAL) - grace period for payment issues
  // 'incomplete' = initial payment not yet completed; grant grace period access so users
  // aren't locked out and can update payment info from the Plan & Payments page
  const trialStatuses = ['payment_failed', 'incomplete'];
  // Statuses that should block access (INACTIVE)
  const inactiveStatuses = ['canceled', 'unpaid', 'past_due', 'incomplete_expired'];

  if (subscriptionStatus) {
    if (activeStatuses.includes(subscriptionStatus)) return AccountStatusTypes.ACTIVE;
    if (trialStatuses.includes(subscriptionStatus)) return AccountStatusTypes.TRIAL;
    if (inactiveStatuses.includes(subscriptionStatus)) return AccountStatusTypes.INACTIVE;

    // Unknown status - log warning and treat as INACTIVE for safety
    // This helps detect new Stripe statuses or data corruption early
    errorReporter.expected('Unknown subscription status', undefined, {
      feature: 'subscription',
      subscriptionStatus,
      knownStatuses: [...activeStatuses, ...trialStatuses, ...inactiveStatuses].join(', '),
    });
    return AccountStatusTypes.INACTIVE;
  }

  // Check for deleted accounts
  if (deletion && now > deletion) return AccountStatusTypes.DELETED;

  // Default to TRIAL if no subscription status (new users, etc.)
  return AccountStatusTypes.TRIAL;
};
